import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ThemeProvider } from '@emotion/react';
import {
  CalendarToday,
  Close as CloseIcon,
  Download,
  Search,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { createTheme } from '@mui/material';

import { InvitationSection } from './components/InvitationSection';
import {
  fetchStaticsMailReminderAPI,
  fetchStaticsMailSenderAPI,
  getDownloadMailsAPI ,
} from './services/statitcsMail';
import { MAIL_STATUS_UI, normalizeMailStats } from './mailStatus';

const MAIL_STATUS_FILTERS = [
  { value: 'Todos', label: 'Todos' },
  ...Object.values(MAIL_STATUS_UI)
    .filter((status) => status.metricField)
    .map((status) => ({
      value: status.label,
      label: status.label,
      color: status.color,
      backgroundColor: status.backgroundColor,
    })),
];

const getMailRowKey = (user, index) =>
  [
    user.correo || 'sin-correo',
    user.estadoOriginal || user.estado || 'sin-estado',
    index,
  ].join('-');

const filterMailRows = (rows = [], searchTerm = '', filterEstado = 'Todos') => {
  const normalizedSearch = searchTerm.trim().toLowerCase();

  return rows.filter((user) => {
    const correo = String(user.correo || '').toLowerCase();
    const matchesSearch =
      normalizedSearch === '' || correo.includes(normalizedSearch);
    const matchesStatus =
      filterEstado === 'Todos' || user.estado === filterEstado;

    return matchesSearch && matchesStatus;
  });
};

const getStatusCounts = (rows = []) =>
  rows.reduce(
    (counts, user) => ({
      ...counts,
      [user.estado]: (counts[user.estado] || 0) + 1,
      Todos: counts.Todos + 1,
    }),
    { Todos: 0 }
  );

function MailDetailsToolbar({
  rows,
  searchTerm,
  setSearchTerm,
  filterEstado,
  setFilterEstado,
}) {
  const statusCounts = getStatusCounts(rows);

  return (
    <Box sx={{ mb: 3 }}>
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Buscar por correo..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: <Search sx={{ mr: 1, color: 'grey.500' }} />,
          sx: { height: 52, fontSize: 16 },
        }}
      />

      <Box
        sx={{
          mt: 2,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {MAIL_STATUS_FILTERS.map((status) => {
          const isSelected = filterEstado === status.value;
          const count = statusCounts[status.value] || 0;

          return (
            <Chip
              key={status.value}
              label={`${status.label} (${count})`}
              clickable
              variant={isSelected ? 'filled' : 'outlined'}
              onClick={() => setFilterEstado(status.value)}
              sx={{
                px: 0.75,
                height: 36,
                fontSize: 14,
                fontWeight: isSelected ? 700 : 500,
                bgcolor: isSelected
                  ? status.backgroundColor || 'primary.main'
                  : 'transparent',
                color: isSelected ? status.color || 'primary.main' : 'text.primary',
                borderColor: status.color || 'divider',
                '&:hover': {
                  bgcolor: status.backgroundColor || 'action.hover',
                },
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
}

function MailDetailsTable({
  rows,
  searchTerm,
  filterEstado,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}) {
  const filteredRows = filterMailRows(rows, searchTerm, filterEstado);
  const visibleRows = filteredRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const emptyRows = Math.max(0, rowsPerPage - visibleRows.length);

  return (
    <Paper sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <TableContainer sx={{ height: 585 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ py: 1.25 }}>
                <strong>Usuario</strong>
              </TableCell>
              <TableCell sx={{ width: 180, py: 1.25 }}>
                <strong>Estado</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((user, index) => (
              <TableRow
                key={getMailRowKey(user, page * rowsPerPage + index)}
                sx={{ height: 53 }}
              >
                <TableCell sx={{ py: 1 }}>{user.correo}</TableCell>
                <TableCell sx={{ py: 1 }}>
                  <Chip
                    label={user.estado}
                    size="small"
                    sx={{
                      bgcolor: user.estadoBackgroundColor,
                      color: user.estadoColor,
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}

            {visibleRows.length === 0 && (
              <TableRow sx={{ height: 530 }}>
                <TableCell colSpan={2} align="center" sx={{ color: 'grey.600' }}>
                  No hay registros para mostrar.
                </TableCell>
              </TableRow>
            )}

            {visibleRows.length > 0 &&
              Array.from({ length: emptyRows }).map((_, index) => (
                <TableRow key={`empty-${index}`} sx={{ height: 53 }}>
                  <TableCell colSpan={2} />
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredRows.length}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[10, 20, 50]}
        labelRowsPerPage="Registros por pagina"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} de ${count !== -1 ? count : `mas de ${to}`}`
        }
      />
    </Paper>
  );
}

export default function StaticsEmails({
  openDialog,
  handleOpenDialog,
  handleCloseDialog,
}) {
  const { id } = useParams();
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [showUserDetailsReminders, setShowUserDetailsReminders] =
    useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [selectedDate, setSelectedDate] = useState('10/04/2025');
  const [selectedSender, setSelectedSender] = useState(null);
  const [selectedReminderDate, setSelectedReminderDate] = useState('');
  const [selectedReminderIndex, setSelectedReminderIndex] = useState(0);
  const [filterEstado, setFilterEstado] = useState('Todos');
  const [detailsPage, setDetailsPage] = useState(0);
  const [detailsRowsPerPage, setDetailsRowsPerPage] = useState(10);
  const [senders, setSenders] = useState({});
  const [reminders, setReminders] = useState({});

  const theme = createTheme({
    palette: {
      primary: {
        main: '#1976d2',
      },
      secondary: {
        main: '#f50057',
      },
      success: {
        main: '#4caf50',
        light: '#e8f5e9',
      },
      warning: {
        main: '#ff9800',
        light: '#fff8e1',
      },
      error: {
        main: '#f44336',
        light: '#ffebee',
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h4: {
        fontWeight: 600,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 500,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          },
        },
      },
    },
  });

  const handleDateChange = (event) => {
    const selected = event.target.value;
    setSelectedDate(selected);

    const sender = senders.envios.find((s) => s.fechaEnvio === selected);
    setSelectedSender(sender);
  };

  const handleReminderDateChange = (event) => {
    setSelectedReminderDate(event.target.value);
    setSelectedReminderIndex(0); // reset index al cambiar fecha
  };

  const handleReminderIndexChange = (event) => {
    setSelectedReminderIndex(Number(event.target.value));
  };

  const fecthMailRemainder = async () => {
    const data = await fetchStaticsMailReminderAPI(id);
    setReminders({ recordatorios: normalizeMailStats(data) });
  };

  const fecthMailSender = async () => {
    const data = await fetchStaticsMailSenderAPI(id);
    setSenders({ envios: normalizeMailStats(data) });
  };

  const getDownloadMails = async (idSurvey, date, sendType) => {
    try {
      const blob = await getDownloadMailsAPI(idSurvey, date, sendType);
  
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
  
      // Define el nombre del archivo dinámico si quieres
      const fileName = `mails_${idSurvey}_${date}_${sendType}.xlsx`;
  
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error descargando archivo:', error);
    }
  };
  

  useEffect(() => {
    fecthMailSender();
    fecthMailRemainder(); // Solo llamas aquí a la API
  }, [openDialog]);

  useEffect(() => {
    if (
      reminders &&
      reminders.recordatorios &&
      reminders.recordatorios.length > 0
    ) {
      const first = reminders.recordatorios[0];
      setSelectedReminderDate(first.fechaEnvio);
      setSelectedReminderIndex(0);
    }
  }, [reminders]); 
  
  
  useEffect(() => {
    if (senders?.envios?.length > 0) {
      const first = senders.envios[0];
      setSelectedDate(first.fechaEnvio);
      setSelectedSender(first);
    }
  }, [senders]);

  useEffect(() => {
    if (!showUserDetails && !showUserDetailsReminders) {
      setFilterEstado('Todos');
      setSearchTerm('');
      setDetailsPage(0);
    }
  }, [showUserDetails, showUserDetailsReminders]);

  useEffect(() => {
    setDetailsPage(0);
  }, [searchTerm, filterEstado, showUserDetails, showUserDetailsReminders]);

  const handleDetailsPageChange = (event, newPage) => {
    setDetailsPage(newPage);
  };

  const handleDetailsRowsPerPageChange = (event) => {
    setDetailsRowsPerPage(parseInt(event.target.value, 10));
    setDetailsPage(0);
  };

  const handleCloseUserDetails = () => {
    setShowUserDetails(false);
    setShowUserDetailsReminders(false);
  };

  return (
    <ThemeProvider theme={theme}>
      {/* Diálogo de confirmación */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg">
        <DialogContent>
          <Box sx={{ maxWidth: 1000, mx: 'auto', p: 4 }}>
            <Tabs
              value={tabValue}
              onChange={(e, newVal) => setTabValue(newVal)}
            >
              <Tab label="Invitaciones" />
              <Tab label="Recordatorios" />
            </Tabs>
            {tabValue === 0 && (
              <>
                <InvitationSection
                  idSurvey={id}
                  selectedDate={selectedDate}
                  handleDateChange={handleDateChange}
                  selectedSender={selectedSender}
                  onOpenDialog={setShowUserDetails}
                  data={senders}
                  getDownloadMails={getDownloadMails}
                  sendType={1}
                />
              </>
            )}

            {tabValue === 1 && (
              <>
                <InvitationSection
                  idSurvey={id}
                  selectedDate={selectedReminderDate}
                  onOpenDialog={setShowUserDetailsReminders}
                  data={reminders}
                  isReminder={true}
                  selectedReminderIndex={selectedReminderIndex}
                  handleReminderDateChange={handleReminderDateChange}
                  handleReminderIndexChange={handleReminderIndexChange}
                  selectedReminderDate={selectedReminderDate}
                  getDownloadMails={getDownloadMails}
                  sendType={2}
                />
              </>
            )}
            <Dialog
              open={showUserDetails}
              onClose={handleCloseUserDetails}
              maxWidth="lg"
              fullWidth
              PaperProps={{
                sx: {
                  height: 'calc(100vh - 48px)',
                  maxHeight: 'calc(100vh - 48px)',
                },
              }}
            >
              <DialogTitle>
                Detalle de Usuarios
                <IconButton
                  aria-label="close"
                  onClick={handleCloseUserDetails}
                  sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent
                dividers
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <MailDetailsToolbar
                  rows={selectedSender?.listaCorreos}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  filterEstado={filterEstado}
                  setFilterEstado={setFilterEstado}
                />

                <MailDetailsTable
                  rows={selectedSender?.listaCorreos}
                  searchTerm={searchTerm}
                  filterEstado={filterEstado}
                  page={detailsPage}
                  rowsPerPage={detailsRowsPerPage}
                  onPageChange={handleDetailsPageChange}
                  onRowsPerPageChange={handleDetailsRowsPerPageChange}
                />
              </DialogContent>
            </Dialog>

            <Dialog
              open={showUserDetailsReminders}
              onClose={handleCloseUserDetails}
              maxWidth="lg"
              fullWidth
              PaperProps={{
                sx: {
                  height: 'calc(100vh - 48px)',
                  maxHeight: 'calc(100vh - 48px)',
                },
              }}
            >
              <DialogTitle>
                Detalle de Usuarios
                <IconButton
                  aria-label="close"
                  onClick={handleCloseUserDetails}
                  sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent
                dividers
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <MailDetailsToolbar
                  rows={
                    tabValue === 0
                      ? selectedSender?.listaCorreos
                      : reminders.recordatorios?.find(
                          (r) => r.fechaEnvio === selectedReminderDate
                        )?.enviosPorFecha?.[selectedReminderIndex]
                        ?.listaCorreos
                  }
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  filterEstado={filterEstado}
                  setFilterEstado={setFilterEstado}
                />

                <MailDetailsTable
                  rows={
                    tabValue === 0
                      ? selectedSender?.listaCorreos
                      : reminders.recordatorios?.find(
                          (r) => r.fechaEnvio === selectedReminderDate
                        )?.enviosPorFecha?.[selectedReminderIndex]
                        ?.listaCorreos
                  }
                  searchTerm={searchTerm}
                  filterEstado={filterEstado}
                  page={detailsPage}
                  rowsPerPage={detailsRowsPerPage}
                  onPageChange={handleDetailsPageChange}
                  onRowsPerPageChange={handleDetailsRowsPerPageChange}
                />
              </DialogContent>
            </Dialog>
          </Box>
        </DialogContent>
      </Dialog>
    </ThemeProvider>
  );
}
